<admin-app-header></admin-app-header>

<div class="aff-page">
    <div class="container-fluid guide">
        <div class="page-header">
            <div>
                <h1><i class="fa-solid fa-handshake"></i> Affiliate programme guide</h1>
                <p class="subtitle">
                    How the programme works, what the system does automatically, and what admins need to do (and avoid). It follows the client's Fee Share Agreement (Referral /
                    Introducer Arrangement). Read this before using the affiliate pages.
                </p>
            </div>
            <div class="page-header__actions">
                <a class="btn btn-secondary" [routerLink]="['/admin/transparency']"><i class="fa-solid fa-eye"></i> Assumptions to review</a>
                <a class="btn btn-secondary" [routerLink]="['/admin/affiliates']"><i class="fa-solid fa-users"></i> Affiliates</a>
                <a class="btn btn-secondary" [routerLink]="['/admin/affiliate-payouts']"><i class="fa-solid fa-money-bill-transfer"></i> Statements &amp; reports</a>
            </div>
        </div>

        <div class="guide-layout">
            <nav class="guide-toc card">
                <strong>On this page</strong>
                <a *ngFor="let s of sections" (click)="scrollTo(s.id)">{{ s.title }}</a>
            </nav>

            <div class="guide-body">
                <!-- Overview -->
                <section class="card" id="overview">
                    <h2>Overview</h2>
                    <p>
                        An affiliate (the agreement calls them the <strong>Referring Party</strong>) is someone who introduces new business to ParkUpp. They earn a
                        <strong>fee share</strong>: a percentage of ParkUpp's <strong>Trading Income</strong> on that business, for 12 months.
                    </p>
                    <p>There are two kinds of introduction:</p>
                    <ul>
                        <li>
                            <strong>Parker introduction:</strong> someone they send signs up and books parking. The affiliate earns on that parker's bookings for 12 months from the
                            parker's <em>first booking</em>.
                        </li>
                        <li>
                            <strong>Building introduction:</strong> a landlord or managing agent they introduce lists a building. The affiliate earns on <em>every bay in that building</em>,
                            whoever books it, for 12 months from the date the building is <em>first listed</em>. This is building by building: introducing a landlord doesn't
                            automatically cover all their other properties.
                        </li>
                    </ul>
                    <div class="callout">
                        <strong>The one rule to remember:</strong> each paid invoice pays at most one fee share. If the building was introduced, the building's affiliate earns and any
                        parker introduction on that invoice earns nothing. The system enforces this; the Overlap report shows every case.
                    </div>
                </section>

                <!-- Numbers -->
                <section class="card" id="numbers">
                    <h2>Key numbers</h2>
                    <p class="muted">Programme defaults. Fee share % and periods can be changed per affiliate; the rest apply to everyone. Items marked * are assumptions the client still needs to confirm.</p>
                    <table class="aff-table facts">
                        <tr>
                            <td>Fee share</td>
                            <td>
                                <strong>{{ d.parkerCommissionPct }}%</strong> (parker) / <strong>{{ d.landlordCommissionPct }}%</strong> (building) of Trading Income
                            </td>
                        </tr>
                        <tr>
                            <td>Trading Income</td>
                            <td>ParkUpp's fee on the rental excl. VAT, less the banking fee (never below R0)</td>
                        </tr>
                        <tr>
                            <td>Banking fee *</td>
                            <td><strong>{{ d.bankingFeePct }}%</strong> of the rental excl. VAT, for every payment method</td>
                        </tr>
                        <tr>
                            <td>Qualifying Period</td>
                            <td>
                                <strong>{{ d.parkerWindowMonths }} months</strong> from a parker's first booking; <strong>{{ d.landlordWindowMonths }} months</strong> from a building's first
                                listing
                            </td>
                        </tr>
                        <tr>
                            <td>Statements</td>
                            <td>Generated by an admin for the previous month (never automatically), paid by EFT by the <strong>{{ d.statementPayByDay }}th</strong>. No minimum, nothing for the affiliate to request.</td>
                        </tr>
                        <tr>
                            <td>Link remembered for *</td>
                            <td><strong>{{ d.attributionWindowDays }} days</strong> after the click (first affiliate link clicked wins)</td>
                        </tr>
                        <tr>
                            <td>Who can be referred by link *</td>
                            <td>Only accounts created in the last <strong>{{ d.claimWindowDays }} days</strong> (new users)</td>
                        </tr>
                        <tr>
                            <td>New only</td>
                            <td>
                                Existing parkers and landlords never count, for any affiliate type. Manual credits are refused if the parker's account, or the landlord's portfolio, is
                                more than <strong>{{ d.attributionWindowDays }} days</strong> old.
                            </td>
                        </tr>
                        <tr>
                            <td>Automatic building credit *</td>
                            <td>Buildings a referred landlord first lists within <strong>{{ d.buildingAutoCreditDays }} days</strong> of being introduced</td>
                        </tr>
                    </table>
                </section>

                <!-- Adding -->
                <section class="card" id="adding">
                    <h2>Adding an affiliate</h2>
                    <ol>
                        <li>Sign the Fee Share Agreement with them first. The system tracks and pays; the agreement is what entitles them to be paid.</li>
                        <li>The person must have a ParkUpp account. If they don't, ask them to sign up first (any account type is fine).</li>
                        <li>Go to <a [routerLink]="['/admin/affiliates']">Marketing → Affiliates</a> and click <strong>New affiliate</strong>.</li>
                        <li>Find them by email, then check the fee share % and periods (the agreement's defaults are filled in).</li>
                        <li>
                            Optionally choose their code (3–20 letters, numbers or dashes). Leave it blank and one is generated, e.g. <code>THABO-7Q2A</code>.
                            <strong>Codes can't be changed later</strong>, because links with the code are already out in the world.
                        </li>
                        <li>Save. An <strong>Affiliate</strong> item appears in their client menu straight away, with their links, their stats, earnings and statements. <strong>Sharing options</strong> opens a pop-up with the QR code (download as PNG or SVG), copy, WhatsApp, email and social sharing. Admins have the same button on the affiliate's detail page.</li>
                        <li>Ask them to add their bank details on that page. Statements can't be paid without them.</li>
                    </ol>
                    <p>Each affiliate gets two links with the same code: a <strong>Parkers link</strong> (home/search page) and a <strong>Landlords link</strong> ("List your parking" page).</p>
                    <h3>Statuses</h3>
                    <ul>
                        <li><strong>Active:</strong> normal.</li>
                        <li><strong>Paused:</strong> new clicks and sign-ups through their link aren't credited for now. Existing introductions keep earning.</li>
                        <li>
                            <strong>Terminated:</strong> the agreement has ended (30 days' notice, §5). No new introductions count, but introductions already made
                            <strong>keep earning for the rest of their 12 months</strong> and keep appearing on statements. To stop an existing introduction (e.g. after a material
                            breach), remove it on the affiliate's detail page with a note.
                        </li>
                    </ul>
                </section>

                <!-- Parker introductions -->
                <section class="card" id="tracking">
                    <h2>Parker introductions</h2>
                    <ol>
                        <li>
                            <strong>Click.</strong> Someone opens a link containing <code>?aff=CODE</code>. The browser remembers the code for {{ d.attributionWindowDays }} days and the
                            visit is counted on the affiliate's stats (one per person per day). If they've already clicked another affiliate's link, the first one is kept.
                        </li>
                        <li>
                            <strong>Sign-up.</strong> When they create an account and sign in on that browser, they're recorded as the affiliate's parker introduction. It's refused if:
                            <ul>
                                <li>the account is older than {{ d.claimWindowDays }} days (existing users can't be "introduced"),</li>
                                <li>it's the affiliate themselves (same account or same email),</li>
                                <li>the user is already someone else's introduction (first one wins), or</li>
                                <li>the affiliate isn't active.</li>
                            </ul>
                        </li>
                        <li>
                            <strong>First booking.</strong> The 12-month Qualifying Period starts when the parker first books (taken from their first paid booking). Signing up alone
                            doesn't start the clock.
                        </li>
                        <li><strong>Earning.</strong> Every invoice that parker pays inside the 12 months earns the affiliate's parker fee share.</li>
                    </ol>
                    <div class="callout callout--warn">
                        <strong>Same device, same browser.</strong> Link tracking relies on the browser remembering the link. If someone clicks on their phone and signs up on a laptop,
                        or clears their browser, it won't be captured. The agreement asks affiliates to tell ParkUpp about introductions in writing, so add those by hand.
                    </div>
                    <h3>Adding a parker introduction by hand</h3>
                    <p>
                        On the affiliate's detail page use <strong>Add a parker</strong>, find the user by email and write a note with the evidence. If the parker already booked, their
                        12 months run from that first booking, so a late entry may already be partly or fully used up. If the user belongs to another affiliate, tick
                        <em>Replace</em>; the old one is kept in history.
                    </p>
                    <p>
                        <strong>Only new parkers count.</strong> Someone who already parked with ParkUpp, or registered before the introduction, can't be credited. The system refuses
                        anyone whose account is more than {{ d.attributionWindowDays }} days old.
                    </p>
                </section>

                <!-- Buildings -->
                <section class="card" id="landlords">
                    <h2>Building introductions</h2>
                    <p>
                        Fee shares are paid <strong>per building</strong>. On <a [routerLink]="['/admin/affiliates']" [queryParams]="{ tab: 'landlords' }">Affiliates → Landlord &amp;
                        building introductions</a> every portfolio is listed with its buildings, the date each was first listed, and who introduced it.
                    </p>
                    <h3>Landlord introductions</h3>
                    <p>
                        A <strong>landlord introduction</strong> records who introduced a landlord. It earns nothing by itself. Its job is to credit that landlord's new buildings
                        automatically: any building in the portfolio first listed within {{ d.buildingAutoCreditDays }} days of the introduction becomes a building introduction for
                        the same affiliate, starting on its listing date.
                    </p>
                    <p>A landlord introduction is recorded automatically when a user who signed up through an affiliate link:</p>
                    <ul>
                        <li>creates a new portfolio, or</li>
                        <li>accepts an invite to a portfolio ParkUpp created less than {{ d.attributionWindowDays }} days earlier.</li>
                    </ul>
                    <h3>Credit by hand when</h3>
                    <ul>
                        <li><strong>ParkUpp set up the portfolio and the landlord never logs in.</strong> Use <em>Introduced by…</em> on the portfolio row, or <em>Credit</em> on each building.</li>
                        <li>A building is listed more than {{ d.buildingAutoCreditDays }} days after the landlord was introduced.</li>
                        <li>The landlord came through an affiliate but not through their link (the affiliate told us in writing).</li>
                        <li>The building was listed before listing dates were tracked. Enter the date it went live.</li>
                    </ul>
                    <p>
                        <strong>Only new landlords count.</strong> A landlord who was already on ParkUpp can't be credited, and nor can their buildings, even new ones. The system refuses
                        a credit if the portfolio is more than {{ d.attributionWindowDays }} days old.
                    </p>
                    <div class="callout callout--warn">
                        <strong>The 12 months run from first listing, not from when you credit it.</strong> If you credit a building months after it went live, part of its period is
                        already used, and invoices paid before you credited it were not counted. Use the Integrity check to pick those up (see below).
                    </div>
                    <p class="muted">Enquiries from the "List your parking" page record the affiliate code the visitor arrived with, which helps confirm who sent a landlord.</p>
                    <p class="muted">An affiliate can't be credited for a portfolio or building they are a member of. That would be a self-referral.</p>
                </section>

                <!-- Security guards -->
                <section class="card" id="guards">
                    <h2>Security guards</h2>
                    <p>
                        Every affiliate has a <strong>type</strong>: <em>Parker promoter</em>, <em>Landlord promoter</em> or <em>Security guard</em>. For promoters the type is just a label
                        showing what they mainly do; both earn the fee share on any introduction. <strong>Security guards are different</strong>: an informal arrangement, paid a flat
                        amount per parker instead of a fee share.
                    </p>
                    <table class="aff-table facts">
                        <tr>
                            <td>Login</td>
                            <td>None. An admin creates the guard on their behalf (name, cellphone, optional email). They never see the affiliate page.</td>
                        </tr>
                        <tr>
                            <td>What they share</td>
                            <td>
                                Their parker link or QR code. Open the guard's page and use <strong>Sharing options</strong> to download the QR code (PNG for printing) or send the link to
                                their phone by WhatsApp.
                            </td>
                        </tr>
                        <tr>
                            <td>What they earn</td>
                            <td>
                                <strong>R{{ d.guardBountyAmount }}</strong> (default) once per parker they bring, when that parker has paid
                                <strong>{{ d.guardBountyPaidInvoices }} invoices</strong> (roughly 3 months of parking). Both numbers can be set per guard.
                            </td>
                        </tr>
                        <tr>
                            <td>Not paid</td>
                            <td>
                                No fee share and nothing for landlords or buildings. <strong>One payout per parker, and that's the end of it</strong>, however long the parker stays. The
                                parker must be new to ParkUpp.
                            </td>
                        </tr>
                        <tr>
                            <td>How they're paid</td>
                            <td>
                                Cash, airtime or EFT. Bounties appear under <a [routerLink]="['/admin/affiliate-payouts']" [queryParams]="{ tab: 'bounties' }">Affiliate Payouts → Guard
                                bounties</a> as soon as they're due. Pay the guard, then click <strong>Mark paid</strong> and record how and the reference. Bounties are separate from
                                the monthly statements.
                            </td>
                        </tr>
                    </table>
                    <h3>Adding a guard</h3>
                    <ol>
                        <li>Marketing → Affiliates → <strong>New affiliate</strong>, type <strong>Security guard</strong>.</li>
                        <li>Enter their name (add the site, e.g. "Sipho (Sandton City gate)"), cellphone, and any notes on how they prefer to be paid.</li>
                        <li>Open their page, click <strong>Sharing options</strong> on the parker link and download or send the QR code.</li>
                    </ol>
                    <div class="callout callout--warn">
                        Parkers must sign up through the guard's link or QR code on their own phone, as a new ParkUpp account. If a parker signs up another way, add them on the guard's
                        page with <strong>Add a parker</strong>. Paid invoices from before that date don't count towards the bounty.
                    </div>
                </section>

                <!-- Commission -->
                <section class="card" id="commission">
                    <h2>How the fee share is worked out</h2>
                    <p>
                        <strong>Trading Income</strong> is what ParkUpp actually earns on a booking: ParkUpp's fee on the rental excl. VAT (the landlord gets the rest), less the
                        <strong>banking fee</strong>. The fee share is a percentage of that. This is the agreement's own worked example, which the system reproduces exactly:
                    </p>
                    <div class="example">
                        <div><span>Invoice paid (incl. VAT)</span><strong>R {{ example.invoice | number: '1.2-2' }}</strong></div>
                        <div><span>Monthly rental (excl. VAT)</span><strong>R {{ example.rental | number: '1.2-2' }}</strong></div>
                        <div><span>ParkUpp's fee ({{ d.defaultPlatformCommissionPct }}%)</span><strong>R {{ example.fee | number: '1.2-2' }}</strong></div>
                        <div><span>Less banking fee ({{ d.bankingFeePct }}%)</span><strong>− R {{ example.banking | number: '1.2-2' }}</strong></div>
                        <div><span>Trading Income</span><strong>R {{ example.tradingIncome | number: '1.2-2' }}</strong></div>
                        <div><span>Fee share ({{ d.landlordCommissionPct }}%)</span><strong>R {{ example.building | number: '1.2-2' }}</strong></div>
                    </div>
                    <ul>
                        <li>
                            <strong>ParkUpp's fee</strong> is the commission rate on the landlord's portfolio <em>as the landlord payout uses it</em>. A portfolio with
                            no rate field at all is treated as 15%. A portfolio whose rate is <em>blank</em> (most self-signup landlords) pays the landlord 100%, so its Trading Income
                            is R0 and no fee share is paid. Set the rate on any portfolio with an introduced building.
                        </li>
                        <li>Everything is fixed at the moment the invoice is marked paid: the rates, the banking fee and the amounts. Later changes only affect later payments (agreement §2).</li>
                        <li>Payments outside an introduction's 12 months earn nothing (shown as "outside earning window").</li>
                        <li>
                            Self-referrals earn nothing: a parker introduction doesn't earn on bays in a portfolio the affiliate belongs to, and a building introduction doesn't earn on
                            invoices the affiliate pays themselves.
                        </li>
                        <li>Each line in the ledger and the CSV export shows the rental, ParkUpp fee, banking fee and Trading Income, so a statement can be checked by hand (§6).</li>
                    </ul>
                </section>

                <!-- Priority -->
                <section class="card" id="priority">
                    <h2>Building vs parker: who gets paid</h2>
                    <p>For every paid invoice the system looks for two possible claims: a building introduction on the building, and a parker introduction on the person paying.</p>
                    <table class="aff-table">
                        <thead>
                            <tr>
                                <th>Situation</th>
                                <th>Who earns</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Only a parker introduction</td>
                                <td>The parker's affiliate</td>
                            </tr>
                            <tr>
                                <td>Only a building introduction</td>
                                <td>The building's affiliate (for every parker in that building)</td>
                            </tr>
                            <tr>
                                <td>Same affiliate introduced both</td>
                                <td>They earn once: the single, consolidated fee share in the agreement (§2)</td>
                            </tr>
                            <tr>
                                <td>Different affiliates introduced the building and the parker</td>
                                <td>The building's affiliate. The parker claim is recorded as <em>suppressed: building introduction has priority</em>. <strong>See the warning below.</strong></td>
                            </tr>
                            <tr>
                                <td>Both, but the building claim is outside its 12 months or a self-referral</td>
                                <td>The parker's affiliate</td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="callout callout--warn">
                        <strong>Not yet covered by the agreement.</strong> The agreement only merges the fee share when the <em>same</em> person introduced both. Giving the building
                        priority when <em>different</em> people introduced them is ParkUpp's rule, and the parker's introducer could argue their own agreement entitles them to be paid.
                        Until a clause is added, review these cases on the Overlap report before paying.
                    </div>
                    <p>
                        Every claim, including the losing one and the reason, is stored with the invoice's record. Only one record can exist per invoice, so paying twice isn't
                        possible. The <a [routerLink]="['/admin/affiliate-payouts']" [queryParams]="{ tab: 'overlap' }">Overlap report</a> lists every building and invoice where
                        claims competed, and an "invoices paid twice" count that must always be 0.
                    </p>
                </section>

                <!-- Statements -->
                <section class="card" id="lifecycle">
                    <h2>Monthly statements</h2>
                    <div class="steps">
                        <div><span class="badge badge--pending">This month</span><p>Invoice paid. Waits for the month to close.</p></div>
                        <div><span class="badge badge--requested">On statement</span><p>An admin generates last month's statements: the month is closed and one statement is created per affiliate.</p></div>
                        <div><span class="badge badge--paid">Paid</span><p>Admin pays by EFT by the {{ d.statementPayByDay }}th and records the reference.</p></div>
                    </div>
                    <ul>
                        <li>There's no minimum and nothing for the affiliate to request (agreement §4).</li>
                        <li>
                            <strong>Nothing runs automatically.</strong> At the start of each month an admin opens Accounts → Affiliate Payouts, picks last month and clicks
                            <strong>Generate statements</strong>. Fee shares from invoices paid later stay as "This month" until a statement is generated. Generating again never
                            duplicates a statement.
                        </li>
                        <li>"Carried forward" means closed but not yet on a statement: from a rejected statement, or a month where refunds outweighed earnings. It goes on the next one.</li>
                    </ul>
                    <h3>Refunds and cancellations</h3>
                    <ul>
                        <li>If an invoice stops being "paid" (refunded, cancelled, set back to due) <strong>before</strong> the statement is paid, its fee share is removed automatically and the statement total drops.</li>
                        <li>If the statement was <strong>already paid</strong>, it's flagged as a clawback and deducted from the affiliate's next statement.</li>
                        <li>If an invoice is marked paid again later, the fee share is recalculated.</li>
                    </ul>
                </section>

                <!-- Payouts -->
                <section class="card" id="payouts">
                    <h2>Paying affiliates</h2>
                    <ol>
                        <li>
                            Early each month (in time to pay by the {{ d.statementPayByDay }}th), go to <a [routerLink]="['/admin/affiliate-payouts']">Accounts → Affiliate Payouts</a>,
                            choose last month and click <strong>Generate statements</strong>. Run the Integrity check first. Slack is notified with the total.
                        </li>
                        <li>Expand each statement to check the invoices it pays and any competing claims. Send the affiliate their statement (agreement §4).</li>
                        <li>Pay by EFT to the bank details shown, by the {{ d.statementPayByDay }}th, then click <strong>Mark paid</strong> and enter the EFT reference.</li>
                        <li>If the statement has no bank details, Mark paid uses the details the affiliate has added since; if they still have none, it can't be marked paid.</li>
                        <li>If something is wrong, <strong>Reject</strong> it with a reason. Its fee shares carry forward to next month's statement.</li>
                    </ol>
                    <p class="muted">Affiliate statements are separate from landlord payouts (Invoice Payouts / Proforma Invoices / Payout Batches) and don't affect them.</p>
                </section>

                <!-- Rules -->
                <section class="card" id="rules">
                    <h2>Do’s and don’ts</h2>
                    <div class="dos">
                        <div>
                            <h3><i class="fa-solid fa-circle-check"></i> Do</h3>
                            <ul>
                                <li>Have a signed Fee Share Agreement before adding an affiliate.</li>
                                <li>Record landlord introductions the day you set up a portfolio for an introduced landlord.</li>
                                <li>Write a note on every manual introduction, replacement or removal: what was introduced and the evidence ("Effective Cause").</li>
                                <li>Set a ParkUpp fee rate on every portfolio with an introduced building.</li>
                                <li>Check the Overlap report before paying statements.</li>
                                <li>Run the <a [routerLink]="['/admin/affiliate-payouts']" [queryParams]="{ tab: 'integrity' }">Integrity check</a> monthly before paying, and after crediting anything late.</li>
                            </ul>
                        </div>
                        <div>
                            <h3><i class="fa-solid fa-circle-xmark"></i> Don’t</h3>
                            <ul>
                                <li>
                                    <strong>Don’t mark invoices paid to test or "tidy up".</strong> Marking an invoice paid creates a fee share for any introduction on it; setting it back
                                    reverses it. Every change shows in the ledger.
                                </li>
                                <li>Don’t tick <em>Replace</em> to move an introduction between affiliates without a clear reason and a note.</li>
                                <li>Don’t credit a building to an affiliate who is a member of that portfolio or works for that landlord.</li>
                                <li>Don’t terminate an affiliate expecting their payments to stop: existing introductions keep earning. Remove introductions individually if they must stop.</li>
                                <li>Don’t pay affiliates outside the statements. The ledger won’t know.</li>
                                <li>Don’t treat link visits as money. Visits are a guide; fee shares come only from paid invoices.</li>
                            </ul>
                        </div>
                    </div>
                </section>

                <!-- Where -->
                <section class="card" id="where">
                    <h2>Where to find things</h2>
                    <table class="aff-table">
                        <tr>
                            <td><a [routerLink]="['/admin/affiliates']">Marketing → Affiliates</a></td>
                            <td>All affiliates, stats and balances; create new affiliates; <strong>Landlord &amp; building introductions</strong> tab.</td>
                        </tr>
                        <tr>
                            <td>Affiliate detail (click a row)</td>
                            <td>Settings, links, bank details, visits chart, introductions (add/remove), full ledger with Trading Income per invoice, statements.</td>
                        </tr>
                        <tr>
                            <td><a [routerLink]="['/admin/affiliate-payouts']">Accounts → Affiliate Payouts</a></td>
                            <td>Monthly statements, parker and building introduction reports, overlap report, integrity check, CSV exports.</td>
                        </tr>
                        <tr>
                            <td>The affiliate's own page</td>
                            <td>Client dashboard → <strong>Affiliate</strong>. Open it from the affiliate detail page ("Their affiliate page") to see what they see.</td>
                        </tr>
                        <tr>
                            <td><a [routerLink]="['/admin/transparency']">Transparency report</a></td>
                            <td>How the build lines up with the Fee Share Agreement, assumptions to confirm, and known gaps.</td>
                        </tr>
                    </table>
                </section>

                <!-- Pending -->
                <section class="card" id="pending">
                    <h2>Assumptions to confirm</h2>
                    <p>
                        These were decided for the build and need the client's confirmation. Each is explained in detail, with what it would take to change, on the
                        <a [routerLink]="['/admin/transparency']">Transparency report</a> under "Affiliate Programme: Fee Share Agreement alignment".
                    </p>
                    <ul>
                        <li>Banking fee is a flat {{ d.bankingFeePct }}% for every payment method.</li>
                        <li>Building introductions take priority over a different affiliate's parker introduction (needs an agreement clause).</li>
                        <li>A landlord introduction auto-credits buildings first listed within {{ d.buildingAutoCreditDays }} days.</li>
                        <li>A parker's 12 months start at their first paid booking.</li>
                        <li>Portfolios with a blank fee rate pay no fee share.</li>
                        <li>Link rules: {{ d.attributionWindowDays }}-day link memory, first link wins, new accounts only.</li>
                    </ul>
                </section>
            </div>
        </div>
    </div>
</div>
